import { useState, useEffect, useCallback } from "react"
import styled from "styled-components"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
`

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: ${props => props.theme.textPrimary};
`

const RefreshButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textPrimary};
  transition: all 0.3s;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    transform: rotate(180deg);
  }
  
  &:active {
    transform: rotate(360deg);
  }
  
  &.refreshing {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`

const SpacesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 12px;
    gap: 6px;
  }
  
  @media (max-width: 350px) {
    padding: 10px;
    gap: 5px;
  }
`

const SpaceCard = styled.div`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 16 / 10;

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    border-radius: 6px;
  }

  @media (max-width: 350px) {
    border-radius: 5px;
  }
`

const SpaceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 25%;
  transition: transform 0.3s ease;
`

const SpaceOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  color: white;
  font-size: 12px;
  font-weight: 500;
`

const LoadingIndicator = styled.div`
  padding: 20px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 16px 16px;
`

const NavButton = styled.button`
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.theme.textPrimary};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.textPrimary};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const PageIndicator = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  flex: 1;
  text-align: center;
`

const SpacesContainer = styled.div`
  position: relative;
`

export default function FeaturedSpaces() {
  const { theme } = useTheme()
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const itemsPerPage = 6;

  const handleImageError = useCallback((url) => {
    setBrokenImages(prev => new Set(prev).add(url));
  }, []);

  const fetchFeaturedSpaces = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/post/featured-spaces?limit=18', {
        credentials: 'include'
      });
      
      const data = await response.json();
      console.log("서버 응답 데이터:", data);
      
      if (data.success && data.spaces) {
        setSpaces(data.spaces);
      }
    } catch (error) {
      console.error("추천 공간 로드 오류:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeaturedSpaces();
  }, []);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(0);
    setBrokenImages(new Set());
    await fetchFeaturedSpaces();
    setTimeout(() => setRefreshing(false), 500);
  };
  
  const validSpaces = spaces.filter(s => s.image_url && !brokenImages.has(s.image_url));
  const totalPages = Math.ceil(validSpaces.length / itemsPerPage);
  const currentSpaces = validSpaces.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };
  
  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>추천 공간</Title>
        <RefreshButton 
          theme={theme}
          onClick={handleRefresh}
          className={refreshing ? 'refreshing' : ''}
          disabled={loading || refreshing}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"></polyline>
            <polyline points="1 20 1 14 7 14"></polyline>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
        </RefreshButton>
      </Header>
      
      {loading ? (
        <LoadingIndicator theme={theme}>추천 공간을 불러오는 중...</LoadingIndicator>
      ) : (
        <>
          <SpacesContainer>
            <SpacesGrid>
              {currentSpaces.map((space) => (
                <SpaceCard key={space.id}>
                  <SpaceImage
                    src={space.image_url}
                    alt={space.name}
                    onError={() => handleImageError(space.image_url)}
                  />
                  <SpaceOverlay>{space.name}</SpaceOverlay>
                </SpaceCard>
              ))}
            </SpacesGrid>
          </SpacesContainer>
          
          {totalPages > 1 && (
            <NavigationContainer>
              <NavButton 
                theme={theme}
                onClick={handlePrevPage} 
                disabled={currentPage === 0}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </NavButton>
              
              <PageIndicator theme={theme}>
                {currentPage + 1} / {totalPages}
              </PageIndicator>
              
              <NavButton 
                theme={theme}
                onClick={handleNextPage} 
                disabled={currentPage === totalPages - 1}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </NavButton>
            </NavigationContainer>
          )}
        </>
      )}
    </Container>
  );
}